<template>
    <div :class="$style.templateBlank">
        <div :class="$style.sidebar">
            <div :class="$style.back" v-if="visibleBack">
                <a :class="$style.link" @click="handleBackClick">
                    <pt-icon
                        icon="pt-icon--back"
                        :style="{
                            width: '18px',
                            height: '18px',
                            fill: '#344563'
                        }"
                    ></pt-icon>
                    <pt-link type="text" color="black" dom-type="a" size="large">
                        <span>{{ $t('header.btn_back') }}</span>
                    </pt-link>
                </a>
                <slot name="tools"></slot>
            </div>
            <div :class="$style.content">
                <h2 :class="$style.title">{{ $t('experience.setting.select_popup_type') }}</h2>
                <div :class="$style.type">
                    <p :class="$style.text">{{ $t('experience.common.type') }}</p>
                    <div :class="$style.typeRadio">
                        <template v-for="card in typeList" :key="card.key">
                            <pt-card
                                shadow="never"
                                :class="[$style.card, currentType === card.key && $style.active]"
                                :body-style="{ padding: '0px' }"
                            >
                                <pt-radio v-model="currentType" :label="card.key">
                                    {{ card.name }}
                                    <pt-icon :icon="card.iconName" :class="$style.icon"></pt-icon>
                                </pt-radio>
                            </pt-card>
                        </template>
                    </div>
                </div>
                <div :class="$style.position">
                    <p :class="$style.text">
                        {{
                            currentType === 'popup'
                                ? $t('experience.setting.popup_position')
                                : $t('experience.setting.stickybar_position')
                        }}
                    </p>

                    <div :class="$style.posContainer" v-if="currentType === 'popup'">
                        <div
                            v-for="pos in positionOptionsRef"
                            :class="[
                                $style.item,
                                $style[pos.row],
                                $style[pos.col],
                                { [$style.active]: pos.code === position }
                            ]"
                            :key="pos.code"
                            @click="handlePositionClick(pos)"
                        ></div>
                    </div>
                    <div :class="$style.stickyPosContainer" v-else>
                        <div
                            :class="[
                                $style.innerBtn,
                                $style.top,
                                { [$style.active]: stickyPosition === 'pos-sticky-top' }
                            ]"
                            @click="handleStickyChange('pos-sticky-top')"
                        ></div>
                        <div
                            :class="[
                                $style.innerBtn,
                                $style.bottom,
                                { [$style.active]: stickyPosition === 'pos-sticky-bottom' }
                            ]"
                            @click="handleStickyChange('pos-sticky-bottom')"
                        ></div>
                    </div>
                    <div
                        v-if="currentType === 'sticky_bar' && stickyPosition === 'pos-sticky-top'"
                        :class="$style.stickyMode">
                        <pt-radio-group v-model="stickyMode">
                            <pt-radio label="inject">
                                {{ $t('experience.setting.stickybar_inject')}}
                                <pt-tooltip
                                    placement="top"
                                    width="300"
                                    :content="$t('experience.setting.stickybar_inject_tip')"
                                    popper-class="trigger-tooltip"
                                >
                                    <pt-icon
                                        icon="pt-icon--help-new"
                                        :style="{ width: '14px', height: '14px', fill: '#091E42' }"
                                        class="pt-ml7"
                                    ></pt-icon>
                                </pt-tooltip>
                            </pt-radio>
                            <pt-radio label="float">
                                {{ $t('experience.setting.stickybar_float')}}
                                <pt-tooltip
                                    placement="top"
                                    width="300"
                                    :content="$t('experience.setting.stickybar_float_tip')"
                                    popper-class="trigger-tooltip"
                                >
                                    <pt-icon
                                        icon="pt-icon--help-new"
                                        :style="{ width: '14px', height: '14px', fill: '#091E42' }"
                                        class="pt-ml7"
                                    ></pt-icon>
                                </pt-tooltip>
                            </pt-radio>
                        </pt-radio-group>
                    </div>
                </div>
                <pt-button
                    type="contain"
                    color="darkgreen"
                    size="medium"
                    class="pt-mt40"
                    @click="handleCreate"
                >
                    {{ $t('experience.setting.next') }}
                </pt-button>
            </div>
        </div>
        <div :class="$style.preview">
            <div :class="$style.content">
                <div :class="$style.tabs">
                    <pt-tabs v-model="currentDevice">
                        <pt-tab-pane name="pc">
                            <template #label>
                                <pt-icon icon="pt-icon-pc" :class="$style.icon"></pt-icon>
                            </template>
                        </pt-tab-pane>
                        <pt-tab-pane name="smartphone">
                            <template #label>
                                <pt-icon icon="pt-icon-phone" :class="$style.icon"></pt-icon>
                            </template>
                        </pt-tab-pane>
                    </pt-tabs>
                </div>
                <div :class="[
                    $style.pc,
                    stickyMode === 'inject' && $style.pcInjected
                ]" v-if="currentDevice === 'pc'">
                    <PcSkeleton size="small"></PcSkeleton>
                    <div
                        :class="[$style.block, position && $style[position]]"
                        v-if="currentType === 'popup'"
                    >
                        <pt-icon icon="pt-icon-clear" :class="$style.closeBtn"></pt-icon>
                    </div>
                    <div
                        :class="[
                            $style.block,
                            $style.stickybar,
                            stickyPosition && $style[stickyPosition]
                        ]"
                        v-else
                    >
                        <pt-icon icon="pt-icon-clear" :class="$style.closeBtn"></pt-icon>
                    </div>
                </div>
                <div :class="$style.phone" v-else>
                    <div :class="[$style.area, stickyMode === 'inject' && $style.phoneInjected]">
                        <PhoneSkeleton size="small"></PhoneSkeleton>
                        <div
                            :class="[$style.block, position && $style[position]]"
                            v-if="currentType === 'popup'"
                        >
                            <pt-icon icon="pt-icon-clear" :class="$style.closeBtn"></pt-icon>
                        </div>
                        <div
                            v-else
                            :class="[
                                $style.block,
                                $style.stickybar,
                                stickyPosition && $style[stickyPosition]
                            ]"
                        >
                            <pt-icon icon="pt-icon-clear" :class="$style.closeBtn"></pt-icon>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import PcSkeleton from '@experience/components/preview/PcSkeleton.vue';
import PhoneSkeleton from '@experience/components/preview/PhoneSkeleton.vue';
export default {
    name: 'ExperienceTemplateBlank',
    components: {
        PcSkeleton,
        PhoneSkeleton
    }
};
</script>

<script lang="ts" setup>
import { ref, computed, onMounted, Ref, watch } from 'vue';
import { cloneDeep } from 'lodash-es';
import { positionOptions, stickyPosOptions } from '@experience/config/setting.config';
import { useI18n } from 'vue-i18n';
import analyticsService from '@assets/services/analytics.service';
import { templateTypeCategoryList } from '@experience/static/config/experience.config';

// interface
interface Props {
    visibleBack: boolean;
}
// props
const props = withDefaults(defineProps<Props>(), {
    visibleBack: false
});

type StickyMode = "inject"|"float"|undefined;

// emits
const emit = defineEmits(['back', 'create', 'createWithTemplate']);
const { t } = useI18n();
// state
const typeList = ref([
    {
        key: 'popup',
        name: t('experience.setting.popup'),
        iconName: 'pt-icon-popup'
    },
    {
        key: 'sticky_bar',
        name: t('experience.setting.stickybar'),
        iconName: 'pt-pic-stickybar'
    }
]);
const positionOptionsRef = ref(positionOptions);
const currentType = ref('popup');
const currentDevice = ref('pc');
const position = ref('pos-center');
const stickyPosition = ref('pos-sticky-top');
const typeCategoryList = ref(cloneDeep(templateTypeCategoryList));
const stickyMode: Ref<StickyMode> = ref();

watch(
    () => currentType.value,
    val => {
        if (val === "sticky_bar") {
            if (stickyPosition.value === "pos-sticky-top") {
                stickyMode.value = stickyMode.value || "inject";
            } else {
                stickyMode.value = undefined;
            }
        } else {
            stickyMode.value = undefined;
        }
    }
)

// function
function handleBackClick() {
    emit('back');
}

function handleCreate() {
    analyticsService
        .usePTX('cp_add_template_blank_select', {
            type: currentType.value,
            position: currentType.value === 'popup' ? position.value : stickyPosition.value
        })
        .track();

    emit(
        'create',
        currentType.value,
        currentType.value === 'popup' ? position.value : stickyPosition.value,
        stickyMode.value || "float",
        currentDevice.value === "pc" ? 80 : 180,
    );
}

function handlePositionClick(pos: { code: string }, type = 'popup') {
    type === 'popup' ? (position.value = pos.code) : (stickyPosition.value = pos.code);
}

function handleStickyChange(type: any) {
    const position = stickyPosOptions.find(item => item.code === type);
    position && handlePositionClick(position, 'sticky_bar');
}
</script>

<style lang="scss" module>
@import '@style/import.scss';
.templateBlank {
    min-height: 728px;
    display: flex;
    .sidebar {
        flex: 0 0 468px;
        .back {
            padding-left: 40px;
            height: 56px;
            display: flex;
            align-items: center;
            transition: 0.2s linear;
            .link {
                display: flex;
                align-items: center;
                color: #344563;
                cursor: pointer;

                a {
                    color: #344563;
                }
                svg {
                    margin-right: 5px;
                    fill: #8993a4;
                }
            }
        }
        .content {
            padding: 40px 70px;
            .title {
                font-size: 32px;
                color: #091e42;
                margin-bottom: 32px;
            }
            .text {
                font-size: 14px;
                margin-bottom: 16px;
            }
            .type {
                .typeRadio {
                    display: flex;
                    justify-content: flex-start;
                    align-items: center;
                    .card {
                        &:nth-of-type(1) {
                            border-radius: 3px 0 0 3px;
                        }
                        &:nth-last-of-type(1) {
                            border-radius: 0 3px 3px 0;
                        }
                        border: 1px solid #ebeef5;
                        width: 116px;
                        &:hover,
                        &.active {
                            border-color: #00a92e;
                        }
                        :global {
                            .pt-radio {
                                width: 100%;
                                padding: 8px;
                            }
                            .pt-radio__label {
                                font-size: 13px;
                            }
                            .pt-icon {
                                display: block;
                                width: 80px;
                                height: 64px;
                                margin: 10px auto 0;
                            }
                        }
                    }
                }
            }
            .position {
                margin-top: 32px;
            }
            .posContainer {
                margin-top: 4px;
                display: grid;
                grid-template-columns: repeat(3, 42px);
                grid-template-rows: repeat(3, 30px);
                column-gap: 3px;
                row-gap: 3px;

                .item {
                    background-color: #ebecf0;
                    border-color: #c1c7d0;
                    border-width: 6px;
                    cursor: pointer;
                    &:hover {
                        background-color: #9cdc9f;
                        border-color: #00a92e;
                    }
                    &.active {
                        background-color: #9cdc9f;
                        border-color: #00a92e;
                    }
                    &.top {
                        border-top-style: solid;
                        &.left {
                            border-top-left-radius: 6px;
                        }
                        &.right {
                            border-top-right-radius: 6px;
                        }
                    }
                    &.bottom {
                        border-bottom-style: solid;
                        &.left {
                            border-bottom-left-radius: 6px;
                        }
                        &.right {
                            border-bottom-right-radius: 6px;
                        }
                    }
                    &.left {
                        border-left-style: solid;
                    }
                    &.right {
                        border-right-style: solid;
                    }
                }
            }

            .stickyMode {
                margin-top: 24px;

                :global {
                    .pt-radio-group {
                        display: flex;
                        flex-direction: column;

                        .pt-radio:last-child {
                            margin-top: 10px;
                        }
                    }
                }

            }
            .stickyPosContainer {
                width: 132px;
                height: 96px;
                border-radius: 6px;
                background-color: #ebecf0;
                position: relative;
                .innerBtn {
                    width: 100%;
                    height: 18px;
                    cursor: pointer;
                    position: absolute;
                    z-index: 2;

                    background-color: #c1c7d0;
                    &:hover {
                        background-color: #00a92e;
                    }
                    &.active {
                        background-color: #00a92e;
                    }
                    &.top {
                        border-radius: 4px 4px 0 0;
                    }
                    &.bottom {
                        bottom: 0;
                        border-radius: 0 0 4px 4px;
                    }
                }
                &::after {
                    content: '';
                    width: 84px;
                    height: 64px;
                    left: 14px;
                    position: absolute;
                    top: 50%;
                    left: 50%;
                    transform: translate(-50%, -50%);
                    border-left: 3px solid $pt-white;
                    border-right: 3px solid $pt-white;
                }
            }
        }
    }
    .preview {
        flex: 1;
        padding: 56px 40px 56px 0;
        border-top-right-radius: 3px;
        border-bottom-right-radius: 3px;
        .content {
            background-color: #f4f5f7;
            border-radius: 8px;
            height: 100%;
            padding: 40px 0;
        }
        .tabs {
            display: flex;
            justify-content: center;
            .icon {
                width: 20px;
                height: 20px;
                fill: #344563;
            }
            :global {
                .pt-tabs {
                    display: inline-block;
                    .pt-tabs__item {
                        padding: 0 10px;
                    }
                    .pt-tabs__item.is-active {
                        svg {
                            fill: #00a92e;
                        }
                    }
                }
            }
        }
        .block {
            position: absolute;
            background-color: #ffffff;
            box-shadow: 5px 5px 9px 0 rgba(0, 0, 0, 0.2);
            .closeBtn {
                position: absolute;
                top: 10px;
                right: 10px;
            }
        }
        .pc {
            background-color: #ffffff;
            border-radius: 3px;
            position: relative;
            width: calc(100% - 100px);
            height: 514px;
            margin: 0 auto;
            overflow: hidden;
            .block {
                width: 340px;
                height: 180px;
                &.stickybar {
                    width: 100%;
                    height: 180px;
                }
                &.pos-center {
                    top: 50%;
                    left: 50%;
                    transform: translate(-50%, -50%);
                }
                &.pos-left-top {
                    top: 12px;
                    left: 12px;
                }
                &.pos-center-top {
                    top: 12px;
                    left: 50%;
                    transform: translateX(-50%);
                }
                &.pos-right-top {
                    top: 12px;
                    right: 12px;
                }
                &.pos-left-center {
                    left: 12px;
                    top: 50%;
                    transform: translateY(-50%);
                }
                &.pos-right-center {
                    right: 12px;
                    top: 50%;
                    transform: translateY(-50%);
                }
                &.pos-left-bottom {
                    bottom: 12px;
                    left: 12px;
                }
                &.pos-center-bottom {
                    bottom: 12px;
                    left: 50%;
                    transform: translateX(-50%);
                }
                &.pos-right-bottom {
                    right: 12px;
                    bottom: 12px;
                }
                &.pos-sticky-top {
                    top: 0;
                    left: 0;
                }
                &.pos-sticky-bottom {
                    bottom: 0;
                    left: 0;
                    &.stickybar {
                        box-shadow: -5px -5px 9px 0 rgb(0 0 0 / 20%);
                    }
                }
            }
            :global {
                .pt-skeleton {
                    header {
                        height: 42px;
                        padding: 12px 0 0 40px;
                    }
                    .banner {
                        height: 204px;
                    }
                }
            }
        }

        .pcInjected {
            padding-top: 80px;

            .block {
                box-shadow: none;
            }
        }

        .phone {
            position: relative;
            width: 280px;
            height: 514px;
            margin: 0 auto;
            overflow: hidden;
            .area {
                background: #fff;
                box-shadow: 0 0 8px 0 RGBA(0, 0, 0, 0.12);
                padding: 17px 20px;
                height: 100%;
                position: relative;
                .block {
                    width: 160px;
                    height: 104px;
                    &.stickybar {
                        width: 100%;
                        height: 180px;
                    }
                    &.pos-center {
                        top: 50%;
                        left: 50%;
                        transform: translate(-50%, -50%);
                    }
                    &.pos-left-top {
                        top: 12px;
                        left: 12px;
                    }
                    &.pos-center-top {
                        top: 12px;
                        left: 50%;
                        transform: translateX(-50%);
                    }
                    &.pos-right-top {
                        top: 12px;
                        right: 12px;
                    }
                    &.pos-left-center {
                        left: 12px;
                        top: 50%;
                        transform: translateY(-50%);
                    }
                    &.pos-right-center {
                        right: 12px;
                        top: 50%;
                        transform: translateY(-50%);
                    }
                    &.pos-left-bottom {
                        bottom: 12px;
                        left: 12px;
                    }
                    &.pos-center-bottom {
                        bottom: 12px;
                        left: 50%;
                        transform: translateX(-50%);
                    }
                    &.pos-right-bottom {
                        right: 12px;
                        bottom: 12px;
                    }
                    &.pos-sticky-top {
                        top: 0;
                        left: 0;
                    }
                    &.pos-sticky-bottom {
                        bottom: 0;
                        left: 0;
                        &.stickybar {
                            box-shadow: -5px -5px 9px 0 rgb(0 0 0 / 20%);
                        }
                    }
                }
            }

            .phoneInjected {
                padding-top: 180px;

                .block {
                    box-shadow: none;
                }
            }
        }
    }
}

:global {
    .trigger-tooltip {
        max-width: 300px;
    }
}
</style>
